import { createBrowserClient } from "@supabase/ssr";
import type { Session, SupabaseClient } from "@supabase/supabase-js";
import { useState } from "react";

export const PRODUCT_MEDIA_BUCKET_NAME = "products-media";
export const STORE_MEDIA_BUCKET_NAME = "store-media";

/**
 * Supabase Storage enforces a strict S3-style subset for object names (spaces, `#`, `?`, etc. fail).
 * @see https://github.com/supabase/storage/issues/133
 */
const DISALLOWED_STORAGE_KEY_CHARS = /[^0-9a-zA-Z!\-_.*'()]/g;

const MAX_STORAGE_FILENAME_LENGTH = 200;

function randomStorageSuffix(): string {
  if (typeof globalThis.crypto?.randomUUID === "function") {
    return globalThis.crypto.randomUUID().slice(0, 8);
  }
  return Date.now().toString(36);
}

/**
 * Single path segment safe for `products-media` / `store-media` keys. ASCII-only; preserves extension when possible.
 * For uniqueness within a folder, use {@link disambiguateStorageFileName}.
 */
export function sanitizeStorageObjectFileName(originalFileName: string): string {
  const trimmed = originalFileName
    .trim()
    .replace(/^[/\\]+/, "")
    .replace(/[/\\]/g, "_");

  if (!trimmed) {
    return `file-${randomStorageSuffix()}.bin`;
  }

  const lastDot = trimmed.lastIndexOf(".");
  let base: string;
  let ext: string;
  if (lastDot > 0 && lastDot < trimmed.length - 1) {
    base = trimmed.slice(0, lastDot);
    ext = trimmed
      .slice(lastDot + 1)
      .replace(DISALLOWED_STORAGE_KEY_CHARS, "")
      .slice(0, 12)
      .toLowerCase();
  } else {
    base = trimmed;
    ext = "";
  }

  let safeBase = base
    .replace(DISALLOWED_STORAGE_KEY_CHARS, "_")
    .replace(/_+/g, "_")
    .replace(/^[._-]+|[._-]+$/g, "");

  if (!safeBase) {
    safeBase = `file-${randomStorageSuffix()}`;
  }

  safeBase = safeBase.slice(0, 180);
  const withExt = ext ? `${safeBase}.${ext}` : safeBase;
  return withExt.slice(0, MAX_STORAGE_FILENAME_LENGTH);
}

/** Filename looks like a raster image we show in galleries (keys in `media_urls`). */
export function isProductMediaImageFileName(fileName: string): boolean {
  const lower = fileName.toLowerCase();
  return (
    lower.endsWith(".png") ||
    lower.endsWith(".jpg") ||
    lower.endsWith(".jpeg") ||
    lower.endsWith(".gif") ||
    lower.endsWith(".webp")
  );
}

/** If `sanitizedName` is already used, append `_2`, `_3`, … before the extension. */
export function disambiguateStorageFileName(
  sanitizedName: string,
  takenNames: Iterable<string>,
): string {
  const taken = new Set(takenNames);
  if (!taken.has(sanitizedName)) {
    return sanitizedName;
  }
  const lastDot = sanitizedName.lastIndexOf(".");
  const stem =
    lastDot > 0 ? sanitizedName.slice(0, lastDot) : sanitizedName;
  const ext = lastDot > 0 ? sanitizedName.slice(lastDot) : "";
  let n = 1;
  let candidate = `${stem}_${n}${ext}`;
  while (taken.has(candidate)) {
    n += 1;
    candidate = `${stem}_${n}${ext}`;
  }
  return candidate;
}

/** Object keys: `store/<store_uuid>/products/<product_id>/<file>` (products-media bucket). */
export function productMediaObjectPath(
  storeId: string,
  productId: string,
  fileName: string,
): string {
  return `store/${storeId}/products/${productId}/${fileName}`;
}

/** Prefix for listing product media objects (no trailing filename). */
export function productMediaFolderPrefix(storeId: string, productId: string): string {
  return `store/${storeId}/products/${productId}`;
}

/** `store/<store_uuid>/banner/<file>` — store must exist (store-media bucket). */
export function storeBannerObjectPath(storeId: string, fileName: string): string {
  return `store/${storeId}/banner/${fileName}`;
}

/**
 * `user/<user_uuid>/banner/<file>` — banner before a store row exists (store-media bucket).
 * RLS limits this folder to the owning auth user.
 */
export function userBannerObjectPath(userId: string, fileName: string): string {
  return `user/${userId}/banner/${fileName}`;
}
export type SupabaseOutletContext = {
  supabase: SupabaseClient;
  domainUrl: string;
  session?: Session;
  googleApiKey?: string;
  /** Cloudflare Turnstile site key. Empty string when not configured (local dev). */
  turnstileSiteKey?: string;
};

type SupabaseEnv = {
  SUPABASE_URL: string;
  SUPABASE_ANON_KEY: string;
};

type UseSupabase = {
  env: SupabaseEnv;
  session: Session | null;
};

export const useSupabase = ({ env }: UseSupabase) => {
  // Singleton
  const [supabase] = useState(() =>
    createBrowserClient(env.SUPABASE_URL!, env.SUPABASE_ANON_KEY!)
  );
  return { supabase };
};

export function getRealTimeSubscription(
  supabase: SupabaseClient,
  callback: () => void
) {
  return supabase
    .channel("realtime posts and likes")
    .on(
      "postgres_changes",
      {
        event: "INSERT",
        schema: "public",
        table: "posts",
      },
      () => {
        callback();
      }
    )
    .subscribe();
}
